import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AlidadeConfigModel } from '../../../core/services/models/alidade-config.model';
import { ApiResponseModel, ApiResponsePaginationModel } from '../../../shared/models/api-response.model';
import {
  buildTemplatesDashboardParams,
  TemplatesDashboardParams,
} from '../../../shared/models/templates-dashboard-params.model';
import { IndReportsTemplateModel } from '../store/indTemplate.model';
import {
  IndividualReportDistributionApiModel,
  IndividualReportsRespondentsFilterModel,
  IndividualReportsRespondentsModel,
} from '../model/individual-reports.model';
import {
  IndividualReportInteractiveFileModel,
  IndividualReportPreviewLinkModel,
  IndividualReportTemplateDashboardModel,
  IndividualReportTemplateModel,
  IndividualReportTemplateUpdateBody,
} from '../model/individual-report-template.model';
import {
  buildDistributionRespondentsParams,
  buildDistributionsDashboardParams,
  DistributionDashboardApiModel,
  DistributionRespondentApiModel,
  DistributionRespondentsParams,
  DistributionsDashboardParams,
} from '../model/distributions-dashboard.model';

@Injectable({
  providedIn: 'root',
})
export class IndividualReportsService {
  private readonly apiUrlReporting: string;

  constructor(
    @Inject('ALIDADE_CONFIG') config: AlidadeConfigModel,
    private http: HttpClient,
  ) {
    this.apiUrlReporting = `${config.apiURL}/reporting`;
  }

  getSurveyDistributions(surveyOid: number): Observable<ApiResponseModel<IndividualReportDistributionApiModel[]>> {
    return this.http.get<ApiResponseModel<IndividualReportDistributionApiModel[]>>(
      `${this.apiUrlReporting}/individualDistributions/${surveyOid}`,
    );
  }

  // Single paginated/filtered/sorted distributions list for the individual-report sending dashboard.
  // Replaces the per-survey getSurveyDistributions fan-out.
  getDistributionsDashboard(
    params?: DistributionsDashboardParams,
  ): Observable<ApiResponsePaginationModel<DistributionDashboardApiModel[]>> {
    return this.http.get<ApiResponsePaginationModel<DistributionDashboardApiModel[]>>(
      `${this.apiUrlReporting}/distributionsDashboard`,
      { params: buildDistributionsDashboardParams(params) },
    );
  }

  // Paginated/filtered/sorted respondents of a single distribution, keyed by distributionOid in the path.
  getDistributionRespondents(
    distributionOid: number,
    params?: DistributionRespondentsParams,
  ): Observable<ApiResponsePaginationModel<DistributionRespondentApiModel[]>> {
    return this.http.get<ApiResponsePaginationModel<DistributionRespondentApiModel[]>>(
      `${this.apiUrlReporting}/distribution/${distributionOid}/respondents`,
      { params: buildDistributionRespondentsParams(params) },
    );
  }

  getSurveyRespondents(
    surveyOid: number,
    data: Partial<IndividualReportsRespondentsFilterModel>,
  ): Observable<ApiResponsePaginationModel<IndividualReportsRespondentsModel[]>> {
    return this.http.post<ApiResponsePaginationModel<IndividualReportsRespondentsModel[]>>(
      `${this.apiUrlReporting}/individualRespondents/${surveyOid}`,
      data,
    );
  }

  getIndividualTemplate(surveyOid: number): Observable<ApiResponseModel<IndReportsTemplateModel[]>> {
    return this.http.get<ApiResponseModel<IndReportsTemplateModel[]>>(
      `${this.apiUrlReporting}/IndividualTemplate/${surveyOid}`,
    );
  }

  updateIndividualTemplate(surveyOid: number, data: IndReportsTemplateModel): Observable<ApiResponseModel<void>> {
    return this.http.put<ApiResponseModel<void>>(`${this.apiUrlReporting}/IndividualTemplate/${surveyOid}`, {
      ...data,
    });
  }

  getIndividualTemplatesDashboard(
    params?: TemplatesDashboardParams,
  ): Observable<ApiResponsePaginationModel<IndividualReportTemplateDashboardModel[]>> {
    return this.http.get<ApiResponsePaginationModel<IndividualReportTemplateDashboardModel[]>>(
      `${this.apiUrlReporting}/individualTemplatesDashboard`,
      { params: buildTemplatesDashboardParams(params) },
    );
  }

  getIndividualReportTemplate(
    individualTemplateOid: number,
    context?: HttpContext,
  ): Observable<ApiResponseModel<IndividualReportTemplateModel[]>> {
    return this.http.get<ApiResponseModel<IndividualReportTemplateModel[]>>(
      `${this.apiUrlReporting}/individualTemplate/${individualTemplateOid}`,
      { context },
    );
  }

  createIndividualReportTemplate(): Observable<ApiResponseModel<IndividualReportTemplateDashboardModel[]>> {
    return this.http.post<ApiResponseModel<IndividualReportTemplateDashboardModel[]>>(
      `${this.apiUrlReporting}/individualTemplate`,
      {},
    );
  }

  updateIndividualReportTemplate(
    individualTemplateOid: number,
    body: IndividualReportTemplateUpdateBody,
    context?: HttpContext,
  ): Observable<ApiResponseModel<void>> {
    return this.http.put<ApiResponseModel<void>>(
      `${this.apiUrlReporting}/individualTemplate/${individualTemplateOid}`,
      body,
      { context },
    );
  }

  deleteIndividualReportTemplate(individualTemplateOid: number): Observable<ApiResponseModel<void>> {
    return this.http.delete<ApiResponseModel<void>>(
      `${this.apiUrlReporting}/individualTemplate/${individualTemplateOid}`,
    );
  }

  copyIndividualReportTemplate(
    individualTemplateOid: number,
  ): Observable<ApiResponseModel<IndividualReportTemplateDashboardModel[]>> {
    return this.http.post<ApiResponseModel<IndividualReportTemplateDashboardModel[]>>(
      `${this.apiUrlReporting}/individualTemplate/${individualTemplateOid}/copy`,
      {},
    );
  }

  fillIndividualReportTemplateDomains(individualTemplateOid: number): Observable<ApiResponseModel<void>> {
    return this.http.put<ApiResponseModel<void>>(
      `${this.apiUrlReporting}/individualTemplate/${individualTemplateOid}/fillDomains`,
      {},
    );
  }

  listIndividualReportFiles(
    individualTemplateOid: number,
    context?: HttpContext,
  ): Observable<ApiResponseModel<IndividualReportInteractiveFileModel[]>> {
    return this.http.get<ApiResponseModel<IndividualReportInteractiveFileModel[]>>(
      `${this.apiUrlReporting}/individualTemplate/${individualTemplateOid}/interactiveReport/files`,
      { context },
    );
  }

  /** Downloads a saved file by its path relative to the report root, e.g. `css/theme.css`. */
  getIndividualReportFile(individualTemplateOid: number, path: string): Observable<string> {
    return this.http.get(this.individualReportFileUrl(individualTemplateOid, path), { responseType: 'text' });
  }

  /** Downloads a saved file by its path relative to the report root; the Blob keeps the response type. */
  getIndividualReportFileBlob(individualTemplateOid: number, path: string, context?: HttpContext): Observable<Blob> {
    return this.http.get(this.individualReportFileUrl(individualTemplateOid, path), { responseType: 'blob', context });
  }

  // encodeURIComponent, unlike HttpParams, also escapes the `/` between folders.
  private individualReportFileUrl(individualTemplateOid: number, path: string): string {
    return `${this.apiUrlReporting}/individualTemplate/${individualTemplateOid}/interactiveReport/file?path=${encodeURIComponent(
      path,
    )}`;
  }

  uploadIndividualReportFile(
    individualTemplateOid: number,
    file: File,
    context?: HttpContext,
  ): Observable<ApiResponseModel<void>> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<ApiResponseModel<void>>(
      `${this.apiUrlReporting}/individualTemplate/${individualTemplateOid}/interactiveReport/files`,
      formData,
      { context },
    );
  }

  deleteIndividualReportFiles(
    individualTemplateOid: number,
    context?: HttpContext,
  ): Observable<ApiResponseModel<void>> {
    return this.http.delete<ApiResponseModel<void>>(
      `${this.apiUrlReporting}/individualTemplate/${individualTemplateOid}/interactiveReport/files`,
      { context },
    );
  }

  // Generates a short-lived admin preview link for a respondent's individual report (default 15 min).
  getRespondentReportPreviewLink(
    respondentOid: number,
  ): Observable<ApiResponseModel<IndividualReportPreviewLinkModel[]>> {
    return this.http.post<ApiResponseModel<IndividualReportPreviewLinkModel[]>>(
      `${this.apiUrlReporting}/respondent/${respondentOid}/previewLink`,
      {},
    );
  }

  sendRespondentIndividualReport(respondentOid: number): Observable<ApiResponseModel<void>> {
    return this.http.post<ApiResponseModel<void>>(
      `${this.apiUrlReporting}/respondent/${respondentOid}/sendIndividualReport`,
      {},
    );
  }

  sendDistributionIndividualReports(distributionOid: number): Observable<ApiResponseModel<void>> {
    return this.http.post<ApiResponseModel<void>>(
      `${this.apiUrlReporting}/distribution/${distributionOid}/sendIndividualReports`,
      {},
    );
  }
}
